import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import type { TopicCondition as TopicConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";
import * as styles from "./conditions.module.css";

export const TopicCondition = memo(function TopicCondition({
  condition,
  onChange,
}: {
  condition: TopicConditionType;
  onChange: (updated: TopicConditionType) => void;
}) {
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const topicsLoad = useLoad(
    () => supportRpcClient.getConversationTopics({}),
    [supportRpcClient],
  );

  const topics = useMemo(
    () => topicsLoad.value?.topics ?? [],
    [topicsLoad.value],
  );

  const topicItems: RedoListItem<string>[] = useMemo(() => {
    return topics.map((topic) => ({
      id: topic,
      type: "text" as const,
      text: topic,
      value: topic,
    }));
  }, [topics]);

  const selectedItems = useMemo(() => {
    return (condition.topics || []).map((topic) => ({
      id: topic,
      type: "text" as const,
      text: topic,
      value: topic,
    }));
  }, [condition.topics]);

  const displayText =
    (condition.topics?.length || 0) > 0
      ? condition.topics!.length === 1
        ? condition.topics![0]
        : `${condition.topics!.length} topics selected`
      : "Select topics";

  return (
    <Flex dir="column" gap="sm" w="full">
      <div style={{ fontSize: "12px", color: "#666" }}>Topic</div>
      <RedoFilterDropdownAnchor
        className={styles.fullWidthButton}
        color="primary"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        ref={setDropdownButtonRef}
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        text={displayText}
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={topicItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          onChange({ ...condition, topics: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
